import { DeleteOutlined, TableOutlined } from '@ant-design/icons'
import type { ViewConfigTreeNode } from '@salescore/core'
import { Alert, Button, Form, List, Select, Tooltip } from 'antd'
import { Array, pipe, Record } from 'effect'
import { t } from 'i18next'
import { type ReactElement, useRef } from 'react'

import { useChildModelsSelectOptions } from '../../../../recoil/view/hooks/kpiForm'
import { useCurrentNodeModels } from '../../../../recoil/view/selectors/kpiFormSelector'
import type { ReferenceToPropertyForAddChildNodeMutation } from '../../ViewUISheet/findSameReferenceToChild'

export const KPI_CHILD_COUNT_WARNING_THRESHOLD = 5

export const KpiFormChildNodeFormItem = ({
  tree,
  updateTree,
}: {
  tree: ViewConfigTreeNode | undefined
  updateTree: (changes: {
    picked?: Array<{ node: ViewConfigTreeNode; referenceToProperty: ReferenceToPropertyForAddChildNodeMutation }>
    unPicked: Array<ViewConfigTreeNode | undefined>
  }) => void
}): ReactElement => {
  const divReference = useRef<HTMLDivElement>(null)
  const currentNodeModels = useCurrentNodeModels(tree)
  const childModelsSelectOptions = useChildModelsSelectOptions(currentNodeModels)
  const childModelsSelectOptionGroups = pipe(
    childModelsSelectOptions,
    Array.groupBy((x) => x.groupLabel),
    Record.toEntries,
    Array.map(([groupLabel, options]) => ({
      label: groupLabel,
      options,
    })),
  )
  const pickedOptions = childModelsSelectOptions.filter((x) => x.existedNode !== undefined)

  return (
    <div tabIndex={-1} ref={divReference}>
      {/* UIの統一のためForm.Itemを使うが、Formの機能は使わず、nameは指定しない */}
      <Form.Item
        // name={namePath}
        label={
          <div>
            <TableOutlined /> {t(`関連付けするオブジェクト`)}
          </div>
        }
        tooltip={
          <div>
            {t(
              `（例）「取引オブジェクト」をベースに選択したとき、会社名を表示するために「会社オブジェクト」を紐づける`,
            )}
          </div>
        }
      >
        {tree === undefined ? (
          <Select disabled />
        ) : (
          <Select
            showSearch
            filterOption={(input, option) =>
              // antd側のバグっており、anyが必要
              // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-type-assertion,@typescript-eslint/strict-boolean-expressions
              (((option as any).key as string) || '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(values) => {
              // updaterパターンが使えない…？
              updateTree({
                picked: pipe(
                  values,
                  Array.flatMapNullishOr((value) => childModelsSelectOptions.find((x) => x.value === value)),
                ),
                unPicked: pipe(
                  pickedOptions,
                  Array.filter((option) => !values.includes(option.value)),
                  Array.map((x) => x.existedNode),
                ),
              })
            }}
            options={childModelsSelectOptionGroups}
            value={pickedOptions.map((x) => x.value)}
            mode="multiple"
            placeholder={`${t(`項目を追加`)}…`}
            tagRender={(properties) => <></>}
            optionRender={(option) => <span className="whitespace-normal break-words ">{option.label}</span>}
          />
        )}
      </Form.Item>
      <KpiChildNodeCountWarning childNodeCount={currentNodeModels.length} />
      {currentNodeModels.length > 0 && (
        <List
          className="mb-6"
          bordered
          dataSource={currentNodeModels.slice(1)}
          renderItem={(item, index) => (
            <List.Item
              actions={[
                <Tooltip key={`node-${item.node.name}-${index}`} placement="right">
                  <Button
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      // 要素の"削除"を行うとフォーカスが外れて保存のショートカットが効かなくなってしまうため、削除後に divにフォーカスを当てる
                      divReference.current?.focus()
                      updateTree({ unPicked: [item.node] })
                    }}
                    type="text"
                  />
                </Tooltip>,
              ]}
            >
              {item.nodePathAsLabel.slice(1).join(': ')} <span className="text-gray-400"> - {item.node.name}</span>
            </List.Item>
          )}
        />
      )}
    </div>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function KpiChildNodeCountWarning({ childNodeCount }: { childNodeCount: number }) {
  // ベースとなるオブジェクトも含まれるため-1する
  if (childNodeCount - 1 <= KPI_CHILD_COUNT_WARNING_THRESHOLD) {
    return <></>
  }
  return (
    <Alert
      message={t(`「関連付けするオブジェクト」の数が多すぎると表示に時間がかかるため、設定数は5以下を推奨しています。`)}
      type="warning"
      showIcon
      closable
      className="mb-2"
    />
  )
}
